import { useCallback } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Modal } from '@hivebrite/design-system/Dialog';
import { Image } from '@hivebrite/design-system/Image';
import { Loader } from '@hivebrite/design-system/Loader';
import { Slider } from '@hivebrite/design-system/Slider';
import { useTheme } from '@hivebrite/design-system/Theme';

import type { StorageFileRecord } from 'hb-react/shared/hooks/useGlobalStorageV2/types';

export interface ImagePreviewModalProps {
  handleCloseModal: () => void;
  images: StorageFileRecord[];
  isOpen: boolean;
  fitToScreen: boolean;
}

export const ImagePreviewModal = ({
  handleCloseModal,
  images = [],
  isOpen,
  fitToScreen = false,
}: ImagePreviewModalProps) => {
  const { themeVariables } = useTheme();

  const renderItemComponent = useCallback(
    ({ default_url: src }: StorageFileRecord) => (
      <Box justifyContent="center">
        <Image src={src} />
      </Box>
    ),
    [],
  );

  const renderLoadingItemComponent = useCallback(
    () => (
      <Box color={themeVariables.frameBackgroundColor}>
        <Loader size={40} />
      </Box>
    ),
    [themeVariables.frameBackgroundColor],
  );

  return (
    <Modal
      bg="transparent"
      center
      closeButtonText={s__(
        'LiveFeedAccessibility|Close preview of the images of the post window',
      )}
      isOpen={isOpen}
      isModal
      onDismiss={handleCloseModal}
    >
      <Wrapper $fitToScreen={fitToScreen}>
        <Slider
          items={images}
          perPageBreakpoints={{ s: 1, m: 1, l: 1, xl: 1 }}
          renderItemComponent={renderItemComponent}
          renderLoadingItemComponent={renderLoadingItemComponent}
          total={images.length}
        />
      </Wrapper>
    </Modal>
  );
};

export default ImagePreviewModal;

const Wrapper = styled(Box)<{ $fitToScreen: boolean }>`
  max-height: inherit;

  /* Slider has margin-left: -12px; margin-right: -12px; */
  max-width: calc(100% - 32px);
  user-select: none;

  ${({ $fitToScreen }) =>
    $fitToScreen &&
    css`
      img {
        /* Horrible hack to make pics in the slider to take space no-more than the screen height */
        max-height: calc(100vh - 32px);
      }
    `}
`;
